import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, DollarSign, CreditCard, Server, AlertCircle, Download, ExternalLink, Copy, Check, User, Building, Globe } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import { companyPaymentsAPI, serversAPI, paymentsAPI, settingsAPI } from '../lib/api';
import { getSupabaseClient, getSupabaseConfig } from '../lib/supabase';
import toast from 'react-hot-toast';

const Calendar = () => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [googleCalendarEnabled, setGoogleCalendarEnabled] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [syncInterval, setSyncInterval] = useState(24);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [webcalUrl, setWebcalUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // fetchReceivables - en başta tanımla (fetchCalendarData tarafından kullanılıyor)
  const fetchReceivables = useCallback(async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return [];
      
      // Receivables'leri customer bilgileri ile birlikte çek
      const { data: receivables, error: receivablesError } = await supabase
        .from('receivables')
        .select(`
          *,
          customers:customer_id(id, name, email, phone)
        `)
        .order('due_date', { ascending: true });
      
      if (receivablesError) {
        console.error('Receivables fetch error:', receivablesError);
        return [];
      }
      
      // Customer bilgilerini ekle
      const receivablesWithCustomer = (receivables || []).map(receivable => {
        const customer = receivable.customers || null;
        return {
          ...receivable,
          customer_name: customer?.name || '',
          customer_email: customer?.email || '',
          customer_phone: customer?.phone || ''
        };
      });
      
      return receivablesWithCustomer;
    } catch (error) {
      console.error('Receivables fetch error:', error);
      return [];
    }
  }, []);

  // generateICSContentFromEvents - syncToGoogleCalendar'dan önce tanımla
  const generateICSContentFromEvents = useCallback((eventsToUse) => {
    if (!eventsToUse || eventsToUse.length === 0) {
      return '';
    }

    let icsContent = 'BEGIN:VCALENDAR\n';
    icsContent += 'VERSION:2.0\n';
    icsContent += 'PRODID:-//Domain Control//Takvim Export//TR\n';
    icsContent += 'CALSCALE:GREGORIAN\n';
    icsContent += 'METHOD:PUBLISH\n';

    eventsToUse.forEach(event => {
      const startDate = new Date(event.date);
      startDate.setHours(9, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setHours(10, 0, 0, 0);

      const formatICSDate = (date) => {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };

      let title = event.title;
      if (event.category === 'income') {
        title = `💰 ${t('calendar.receivable')}: ${title}`;
      } else {
        title = `💸 ${t('calendar.payable')}: ${title}`;
      }

      let description = `${t('calendar.amount')}: ${formatCurrency(event.amount, event.currency)}\\n`;
      if (event.company) description += `${t('calendar.company')}: ${event.company}\\n`;
      if (event.domain) description += `${t('calendar.domain')}: ${event.domain}\\n`;
      if (event.provider) description += `${t('calendar.provider')}: ${event.provider}\\n`;
      if (event.frequency) {
        const freqText = event.frequency === 'monthly' ? t('calendar.monthly') : event.frequency === 'yearly' ? t('calendar.yearly') : t('calendar.oneTime');
        description += `${t('calendar.frequency')}: ${freqText}\\n`;
      }
      if (event.notes) description += `${t('calendar.notes')}: ${event.notes}\\n`;
      if (event.description) description += `${t('calendar.description')}: ${event.description}\\n`;

      const escapeICS = (text) => {
        return text
          .replace(/\\/g, '\\\\')
          .replace(/;/g, '\\;')
          .replace(/,/g, '\\,')
          .replace(/\n/g, '\\n');
      };

      icsContent += 'BEGIN:VEVENT\n';
      icsContent += `UID:${event.id}@domaincontrol.com\n`;
      icsContent += `DTSTART:${formatICSDate(startDate)}\n`;
      icsContent += `DTEND:${formatICSDate(endDate)}\n`;
      icsContent += `SUMMARY:${escapeICS(title)}\n`;
      icsContent += `DESCRIPTION:${escapeICS(description)}\n`;
      icsContent += `DTSTAMP:${formatICSDate(new Date())}\n`;
      icsContent += 'END:VEVENT\n';
    });

    icsContent += 'END:VCALENDAR\n';
    return icsContent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // fetchCalendarData - fetchReceivables kullanıyor, useEffect'lerden önce tanımlanmalı
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Tüm verileri paralel olarak çek
      const [companyPayments, servers, payments, receivables] = await Promise.all([
        companyPaymentsAPI.getAll(),
        serversAPI.getAll(),
        paymentsAPI.getAll(),
        fetchReceivables()
      ]);

      const allEvents = [];

      // Tekrarlı ödemeler (company_payments) - next_payment_date
      // Müşterilerden alacağımız ödemeler - ALACAK
      if (companyPayments && Array.isArray(companyPayments)) {
        companyPayments.forEach(payment => {
          // is_active kontrolü: null/undefined ise true kabul et (default active)
          const isActive = payment.is_active !== false && payment.is_active !== '0' && payment.is_active !== 0;
          
          if (!isActive) {
            return;
          }
          
          // next_payment_date hesaplama: öncelik sırası: next_payment_date > last_payment_date > start_date
          let paymentDate = null;
          
          // 1. Öncelik: next_payment_date varsa kullan
          if (payment.next_payment_date) {
            try {
              // Tarihi yerel timezone'da parse et (timezone kaymasını önlemek için)
              const dateStr = payment.next_payment_date;
              if (dateStr.includes('T')) {
                // ISO formatında geliyorsa (2026-01-01T00:00:00Z)
                paymentDate = new Date(dateStr);
              } else {
                // Sadece tarih geliyorsa (2026-01-01) yerel timezone'da parse et
                const [year, month, day] = dateStr.split('-').map(Number);
                paymentDate = new Date(year, month - 1, day);
              }
              if (isNaN(paymentDate.getTime())) {
                paymentDate = null;
              }
            } catch (error) {
              console.error('❌ next_payment_date parse hatası:', error, payment);
              paymentDate = null;
            }
          }
          
          // 2. next_payment_date yoksa, last_payment_date varsa ondan sonraki tarihi hesapla
          if (!paymentDate && payment.last_payment_date && payment.frequency) {
            try {
              // Tarihi yerel timezone'da parse et
              const dateStr = payment.last_payment_date;
              let lastDate;
              if (dateStr.includes('T')) {
                lastDate = new Date(dateStr);
              } else {
                const [year, month, day] = dateStr.split('-').map(Number);
                lastDate = new Date(year, month - 1, day);
              }
              if (!isNaN(lastDate.getTime())) {
                paymentDate = new Date(lastDate);
                
                // Frequency'ye göre last_payment_date'den sonraki tarihi hesapla
                if (payment.frequency === 'monthly') {
                  paymentDate.setMonth(paymentDate.getMonth() + 1);
                } else if (payment.frequency === 'yearly') {
                  paymentDate.setFullYear(paymentDate.getFullYear() + 1);
                } else if (payment.frequency === 'one-time') {
                  // One-time için last_payment_date'i kullan
                  paymentDate = lastDate;
                }
                
                // Eğer hesaplanan tarih geçmişteyse, bir sonraki periyodu hesapla
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                if (paymentDate < today && payment.frequency !== 'one-time') {
                  // Geçmiş tarihse, gelecekteki ilk tarihi bul
                  while (paymentDate < today) {
                    if (payment.frequency === 'monthly') {
                      paymentDate.setMonth(paymentDate.getMonth() + 1);
                    } else if (payment.frequency === 'yearly') {
                      paymentDate.setFullYear(paymentDate.getFullYear() + 1);
                    } else {
                      break;
                    }
                  }
                }
              }
            } catch (error) {
              console.error('❌ last_payment_date parse hatası:', error, payment);
            }
          }
          
          // 3. last_payment_date de yoksa, start_date ve frequency'den hesapla
          if (!paymentDate && payment.start_date && payment.frequency) {
            try {
              // Tarihi yerel timezone'da parse et
              const dateStr = payment.start_date;
              let startDate;
              if (dateStr.includes('T')) {
                startDate = new Date(dateStr);
              } else {
                const [year, month, day] = dateStr.split('-').map(Number);
                startDate = new Date(year, month - 1, day);
              }
              if (!isNaN(startDate.getTime())) {
                paymentDate = new Date(startDate);
                
                // Frequency'ye göre next_payment_date'i hesapla
                if (payment.frequency === 'monthly') {
                  paymentDate.setMonth(paymentDate.getMonth() + 1);
                } else if (payment.frequency === 'yearly') {
                  paymentDate.setFullYear(paymentDate.getFullYear() + 1);
                } else if (payment.frequency === 'one-time') {
                  // One-time için start_date'i kullan
                  paymentDate = startDate;
                }
                
                // Eğer hesaplanan tarih geçmişteyse, bir sonraki periyodu hesapla
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                if (paymentDate < today && payment.frequency !== 'one-time') {
                  // Geçmiş tarihse, gelecekteki ilk tarihi bul
                  while (paymentDate < today) {
                    if (payment.frequency === 'monthly') {
                      paymentDate.setMonth(paymentDate.getMonth() + 1);
                    } else if (payment.frequency === 'yearly') {
                      paymentDate.setFullYear(paymentDate.getFullYear() + 1);
                    } else {
                      break;
                    }
                  }
                }
              }
            } catch (error) {
              console.error('❌ start_date parse hatası:', error, payment);
            }
          }
          
          // paymentDate varsa event ekle
          if (paymentDate && !isNaN(paymentDate.getTime())) {
            // Title'a firma ismini ekle
            let eventTitle = payment.package_name || t('calendar.recurringPayment');
            if (payment.company_name) {
              eventTitle = `${eventTitle} - ${payment.company_name}`;
            }
            
            const event = {
              id: `payment-${payment.id}`,
              date: paymentDate,
              type: 'recurring_payment',
              title: eventTitle,
              amount: payment.amount || payment.sale_price || 0,
              currency: payment.currency || payment.sale_currency || 'TRY',
              company: payment.company_name || '',
              domain: payment.domain_name || '',
              frequency: payment.frequency || '',
              category: 'income' // Alacak - Müşterilerden alacağımız ödemeler
            };
            allEvents.push(event);
          }
        });
      }

      // Sunucu ödemeleri (servers) - expiry_date
      if (servers && Array.isArray(servers)) {
        servers.forEach(server => {
          if (server.expiry_date) {
            allEvents.push({
              id: `server-${server.id}`,
              date: new Date(server.expiry_date),
              type: 'server_payment',
              title: server.name || t('calendar.serverPayment'),
              amount: server.cost || 0,
              currency: server.currency || 'USD',
              provider: server.provider || '',
              plan: server.plan || '',
              category: 'expense' // Verecek
            });
          }
        });
      }

      // Ödemeler (payments) - due_date
      // Müşterilerden alacağımız ödemeler - ALACAK
      if (payments && Array.isArray(payments)) {
        payments.forEach(payment => {
          if (payment.due_date && payment.status !== 'paid') {
            allEvents.push({
              id: `payment-due-${payment.id}`,
              date: new Date(payment.due_date),
              type: 'payment_due',
              title: payment.payment_type || t('calendar.payment'),
              amount: payment.amount || 0,
              currency: payment.currency || 'TRY',
              invoice_number: payment.invoice_number || '',
              notes: payment.notes || '',
              category: 'income' // Alacak - Müşterilerden alacağımız ödemeler
            });
          }
        });
      }

      // Alacaklar (receivables) - due_date
      if (receivables && Array.isArray(receivables)) {
        receivables.forEach(receivable => {
          if (receivable.due_date && receivable.status !== 'received') {
            // Title'a müşteri ismini ekle
            let eventTitle = receivable.description || t('calendar.receivable');
            if (receivable.customer_name) {
              eventTitle = `${eventTitle} - ${receivable.customer_name}`;
            }
            
            allEvents.push({
              id: `receivable-${receivable.id}`,
              date: new Date(receivable.due_date),
              type: 'receivable',
              title: eventTitle,
              amount: receivable.amount || 0,
              currency: receivable.currency || 'TRY',
              description: receivable.description || '',
              customer_name: receivable.customer_name || '',
              customer_email: receivable.customer_email || '',
              customer_phone: receivable.customer_phone || '',
              status: receivable.status || 'pending',
              received_date: receivable.received_date || null,
              created_at: receivable.created_at || null,
              category: 'income' // Alacak
            });
          }
        });
      }

      setEvents(allEvents);
      return allEvents; // Event'leri döndür
    } catch (error) {
      console.error('Calendar data fetch error:', error);
      toast.error(t('calendar.fetchError') + ': ' + (error.message || t('common.unknownError')));
      return []; // Hata durumunda boş array döndür
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReceivables, t]);

  // syncToGoogleCalendar - fetchCalendarData ve generateICSContentFromEvents kullanıyor
  const syncToGoogleCalendar = useCallback(async () => {
    if (isSyncing) return;
    
    try {
      setIsSyncing(true);
      
      // Her zaman fresh data çek (state güncellemesi asenkron olduğu için)
      const fetchedEvents = await fetchCalendarData();
      
      if (!fetchedEvents || fetchedEvents.length === 0) {
        return;
      }

      // ICS dosyası oluştur (sessiz mod - otomatik indirme yok)
      // generateICSContent yerine direkt fetchedEvents kullan
      const icsContent = generateICSContentFromEvents(fetchedEvents);
      if (!icsContent) {
        return;
      }
      
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      // Sessiz mod: Dosyayı otomatik indirme, sadece hazır olduğunu bildir
      // Kullanıcı isterse manuel olarak indirebilir
      setLastSyncTime(new Date());
      
      // İsteğe bağlı: Sessiz toast bildirimi
      toast.success(t('calendar.calendarUpdated', { count: fetchedEvents.length }), {
        duration: 2000,
        icon: '🔄'
      });
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Google Calendar sync error:', error);
      toast.error(t('calendar.syncError') + ': ' + (error.message || t('common.unknownError')), { duration: 3000 });
    } finally {
      setIsSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSyncing, fetchCalendarData, generateICSContentFromEvents, t]);

  const generateWebcalUrl = useCallback(() => {
    try {
      const config = getSupabaseConfig();
      if (config.url) {
        // Webcal URL'i oluştur
        const baseUrl = window.location.origin;
        const webcalUrl = `webcal://${baseUrl.replace(/^https?:\/\//, '')}/api/calendar.ics`;
        setWebcalUrl(webcalUrl);
      }
    } catch (error) {
      console.error('Webcal URL could not be created:', error);
    }
  }, []);

  const fetchGoogleCalendarSetting = useCallback(async () => {
    try {
      const settings = await settingsAPI.getAll();
      const enabled = settings.google_calendar_enabled === '1' || settings.google_calendar_enabled === true;
      const autoSync = settings.google_calendar_auto_sync === '1' || settings.google_calendar_auto_sync === true;
      const interval = parseInt(settings.google_calendar_sync_interval || '24', 10);
      
      setGoogleCalendarEnabled(enabled);
      setAutoSyncEnabled(autoSync);
      setSyncInterval(interval);
    } catch (error) {
      console.error('Google Calendar setting could not be fetched:', error);
    }
  }, []);

  // currentDate değiştiğinde sadece yıl ve ay değişikliğini kontrol et
  // Date objesi referans sorunu yaratmaması için primitive değerlere çevir
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Sadece yıl ve ay değiştiğinde verileri yeniden yükle
  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear, currentMonth]);

  // Sadece bir kez çalışacak ayarlar (mount olduğunda)
  useEffect(() => {
    fetchGoogleCalendarSetting();
    generateWebcalUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyWebcalUrl = () => {
    if (webcalUrl) {
      navigator.clipboard.writeText(webcalUrl).then(() => {
        setCopied(true);
        toast.success(t('calendar.webcalUrlCopied'));
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        toast.error(t('calendar.urlCopyError'));
      });
    }
  };

  useEffect(() => {
    if (autoSyncEnabled && googleCalendarEnabled) {
      // İlk senkronizasyonu hemen yap
      syncToGoogleCalendar();
      
      // Periyodik senkronizasyon için interval kur
      const intervalMs = syncInterval * 60 * 60 * 1000; // Saat cinsinden milisaniyeye çevir
      const intervalId = setInterval(() => {
        syncToGoogleCalendar();
      }, intervalMs);

      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSyncEnabled, googleCalendarEnabled, syncInterval]);


  // ICS içeriğini oluştur (exportToICS'den ayrı fonksiyon)
  const generateICSContent = () => {
    if (events.length === 0) {
      return '';
    }

    let icsContent = 'BEGIN:VCALENDAR\n';
    icsContent += 'VERSION:2.0\n';
    icsContent += 'PRODID:-//Domain Control//Takvim Export//TR\n';
    icsContent += 'CALSCALE:GREGORIAN\n';
    icsContent += 'METHOD:PUBLISH\n';

    events.forEach(event => {
      const startDate = new Date(event.date);
      startDate.setHours(9, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setHours(10, 0, 0, 0);

      const formatICSDate = (date) => {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };

      let title = event.title;
      if (event.category === 'income') {
        title = `💰 ${t('calendar.receivable')}: ${title}`;
      } else {
        title = `💸 ${t('calendar.payable')}: ${title}`;
      }

      let description = `${t('calendar.amount')}: ${formatCurrency(event.amount, event.currency)}\\n`;
      if (event.company) description += `${t('calendar.company')}: ${event.company}\\n`;
      if (event.domain) description += `${t('calendar.domain')}: ${event.domain}\\n`;
      if (event.provider) description += `${t('calendar.provider')}: ${event.provider}\\n`;
      if (event.frequency) {
        const freqText = event.frequency === 'monthly' ? t('calendar.monthly') : event.frequency === 'yearly' ? t('calendar.yearly') : t('calendar.oneTime');
        description += `${t('calendar.frequency')}: ${freqText}\\n`;
      }
      if (event.notes) description += `${t('calendar.notes')}: ${event.notes}\\n`;
      if (event.description) description += `${t('calendar.description')}: ${event.description}\\n`;

      const escapeICS = (text) => {
        return text
          .replace(/\\/g, '\\\\')
          .replace(/;/g, '\\;')
          .replace(/,/g, '\\,')
          .replace(/\n/g, '\\n');
      };

      icsContent += 'BEGIN:VEVENT\n';
      icsContent += `UID:${event.id}@domaincontrol.com\n`;
      icsContent += `DTSTART:${formatICSDate(startDate)}\n`;
      icsContent += `DTEND:${formatICSDate(endDate)}\n`;
      icsContent += `SUMMARY:${escapeICS(title)}\n`;
      icsContent += `DESCRIPTION:${escapeICS(description)}\n`;
      icsContent += `DTSTAMP:${formatICSDate(new Date())}\n`;
      icsContent += 'END:VEVENT\n';
    });

    icsContent += 'END:VCALENDAR\n';
    return icsContent;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Önceki ayın son günleri
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }
    
    // Mevcut ayın günleri
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }
    
    // Sonraki ayın ilk günleri (takvimi tamamlamak için)
    const remainingDays = 42 - days.length; // 6 hafta x 7 gün = 42
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    // Yerel timezone'da tarih karşılaştırması yap (timezone kaymasını önlemek için)
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();
    return events.filter(event => {
      const eventYear = event.date.getFullYear();
      const eventMonth = event.date.getMonth();
      const eventDay = event.date.getDate();
      return eventYear === dateYear && eventMonth === dateMonth && eventDay === dateDay;
    });
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dayEvents = getEventsForDate(date);
    setSelectedEvents(dayEvents);
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'recurring_payment':
        return <CreditCard className="h-4 w-4" />;
      case 'server_payment':
        return <Server className="h-4 w-4" />;
      case 'payment_due':
        return <DollarSign className="h-4 w-4" />;
      case 'receivable':
        return <DollarSign className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getEventColor = (category, type) => {
    if (category === 'income') {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700';
    } else {
      if (type === 'recurring_payment') {
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700';
      } else if (type === 'server_payment') {
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700';
      } else {
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700';
      }
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbols = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    const symbol = symbols[currency] || currency;
    return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Google Calendar URL oluştur (tek bir event için)
  const createGoogleCalendarURL = (event) => {
    const startDate = new Date(event.date);
    startDate.setHours(9, 0, 0, 0); // Sabah 9:00
    const endDate = new Date(startDate);
    endDate.setHours(10, 0, 0, 0); // 1 saatlik event

    // Google Calendar URL formatı: YYYYMMDDTHHmmssZ
    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    let title = event.title;
    if (event.category === 'income') {
      title = `💰 ${t('calendar.receivable')}: ${title}`;
    } else {
      title = `💸 ${t('calendar.payable')}: ${title}`;
    }

    let description = `${t('calendar.amount')}: ${formatCurrency(event.amount, event.currency)}\n`;
    if (event.company) description += `${t('calendar.company')}: ${event.company}\n`;
    if (event.domain) description += `${t('calendar.domain')}: ${event.domain}\n`;
    if (event.provider) description += `${t('calendar.provider')}: ${event.provider}\n`;
    if (event.frequency) {
      const freqText = event.frequency === 'monthly' ? t('calendar.monthly') : event.frequency === 'yearly' ? t('calendar.yearly') : t('calendar.oneTime');
      description += `${t('calendar.frequency')}: ${freqText}\n`;
    }
    if (event.notes) description += `${t('calendar.notes')}: ${event.notes}\n`;
    if (event.description) description += `${t('calendar.description')}: ${event.description}\n`;

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${start}/${end}`,
      details: description,
      sf: 'true',
      output: 'xml'
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // ICS dosyası oluştur ve indir
  const exportToICS = () => {
    if (events.length === 0) {
      toast.error(t('calendar.noEventsToExport'));
      return;
    }

    const icsContent = generateICSContent();
    if (!icsContent) {
      toast.error(t('calendar.icsCreateError'));
      return;
    }

    // Dosyayı indir
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `domain_control_calendar_${new Date().toISOString().split('T')[0]}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(t('calendar.exportSuccess'));
  };

  // Google Calendar'a tek tek ekle (her event için ayrı link)
  const addToGoogleCalendar = (event) => {
    const url = createGoogleCalendarURL(event);
    window.open(url, '_blank');
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    t('calendar.january', 'Ocak'), t('calendar.february', 'Şubat'), t('calendar.march', 'Mart'),
    t('calendar.april', 'Nisan'), t('calendar.may', 'Mayıs'), t('calendar.june', 'Haziran'),
    t('calendar.july', 'Temmuz'), t('calendar.august', 'Ağustos'), t('calendar.september', 'Eylül'),
    t('calendar.october', 'Ekim'), t('calendar.november', 'Kasım'), t('calendar.december', 'Aralık')
  ];
  const dayNames = [
    t('calendar.sun', 'Paz'), t('calendar.mon', 'Pzt'), t('calendar.tue', 'Sal'),
    t('calendar.wed', 'Çar'), t('calendar.thu', 'Per'), t('calendar.fri', 'Cum'), t('calendar.sat', 'Cmt')
  ];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <CalendarIcon className="h-8 w-8 mr-3 text-blue-600 dark:text-blue-400" />
              {t('calendar.title', 'Ödeme Takvimi')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('calendar.subtitle', 'Tüm ödemelerinizi takviminizde görün')}</p>
          </div>
          {googleCalendarEnabled && (
            <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-3 lg:space-y-0 lg:space-x-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={exportToICS}
                  className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  title={t('calendar.exportToICS', 'ICS Olarak Dışa Aktar')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('calendar.downloadICS', 'ICS Olarak İndir')}
                </button>
                {webcalUrl && (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <input
                        type="text"
                        value={webcalUrl}
                        readOnly
                        className="bg-transparent border-none outline-none text-xs text-blue-700 dark:text-blue-300 font-mono w-32 sm:w-40 lg:w-64"
                        title={t('calendar.webcalUrlTitle')}
                      />
                      <button
                        onClick={copyWebcalUrl}
                        className="ml-2 p-1 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded transition-colors"
                        title={t('calendar.copyUrl')}
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        )}
                      </button>
                    </div>
                    <a
                      href={webcalUrl.replace('webcal://', 'https://')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                      title={t('calendar.openWebcalUrl')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('calendar.webcal')}
                    </a>
                  </div>
                )}
              </div>
              {autoSyncEnabled && (
                <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-blue-700 dark:text-blue-300">
                    {t('calendar.autoSyncEnabled', 'Otomatik Senkronizasyon Aktif')}
                  </span>
                  {lastSyncTime && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      ({t('calendar.lastSync', 'Son')}: {lastSyncTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Takvim */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          {/* Takvim Başlığı */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={handleToday}
                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                {t('calendar.today', 'Bugün')}
              </button>
            </div>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Gün İsimleri */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div
                key={day}
                className="text-center text-sm font-semibold text-gray-600 dark:text-gray-400 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Takvim Günleri */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const dayEvents = getEventsForDate(day.date);
              // Yerel timezone'da tarih karşılaştırması yap
              const todayYear = today.getFullYear();
              const todayMonth = today.getMonth();
              const todayDay = today.getDate();
              const isToday = day.date.getFullYear() === todayYear &&
                              day.date.getMonth() === todayMonth &&
                              day.date.getDate() === todayDay;
              const isSelected = selectedDate && 
                                day.date.getFullYear() === selectedDate.getFullYear() &&
                                day.date.getMonth() === selectedDate.getMonth() &&
                                day.date.getDate() === selectedDate.getDate();
              
              return (
                <div
                  key={index}
                  onClick={() => handleDateClick(day.date)}
                  className={`
                    min-h-[100px] p-2 rounded-lg border cursor-pointer transition-colors
                    ${day.isCurrentMonth 
                      ? 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600' 
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700 opacity-50'
                    }
                    ${isToday ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}
                    ${isSelected ? 'ring-2 ring-red-500 dark:ring-red-400' : ''}
                  `}
                >
                  <div className={`
                    text-sm font-medium mb-1
                    ${day.isCurrentMonth 
                      ? 'text-gray-900 dark:text-white' 
                      : 'text-gray-400 dark:text-gray-500'
                    }
                    ${isToday ? 'text-blue-600 dark:text-blue-400 font-bold' : ''}
                  `}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map(event => (
                      <div
                        key={event.id}
                        className={`
                          text-xs px-1.5 py-0.5 rounded border
                          ${getEventColor(event.category, event.type)}
                        `}
                        title={event.title}
                      >
                        <div className="flex items-center">
                          {getEventIcon(event.type)}
                          <span className="ml-1 truncate flex-1">
                            {event.type === 'recurring_payment' && event.company
                              ? event.company
                              : event.type === 'receivable' && event.customer_name
                              ? event.customer_name
                              : event.title}
                          </span>
                        </div>
                        {(event.type === 'recurring_payment' && event.company) || 
                         (event.type === 'receivable' && event.customer_name) ? (
                          <div className="text-[10px] mt-0.5 opacity-80 truncate">
                            {event.type === 'recurring_payment' 
                              ? event.title.split(' - ')[0] 
                              : event.description || event.title}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 px-1.5">
                        +{dayEvents.length - 3} {t('common.more', 'daha')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Seçili Gün Detayları */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {selectedDate 
              ? `${selectedDate.getDate()} ${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
              : t('common.selectDate', 'Bir gün seçin')
            }
          </h3>

          {selectedDate && selectedEvents.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('calendar.noEventsForDate', 'Bu tarih için ödeme yok')}</p>
          )}

          {selectedEvents.length > 0 && (
            <div className="space-y-3">
              {/* Alacaklar */}
              {selectedEvents.filter(e => e.category === 'income').length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    {t('calendar.receivables', 'Alacaklar')}
                  </h4>
                  <div className="space-y-2">
                    {selectedEvents
                      .filter(e => e.category === 'income')
                      .map(event => (
                        <div
                          key={event.id}
                          className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-green-900 dark:text-green-200 text-sm">
                                {event.title}
                              </div>
                              {event.type === 'receivable' && event.customer_name && (
                                <div className="text-green-700 dark:text-green-300 text-xs mt-1 flex items-center">
                                  <User className="h-3 w-3 mr-1" />
                                  {event.customer_name}
                                </div>
                              )}
                              {event.type === 'recurring_payment' && event.company && (
                                <div className="text-green-700 dark:text-green-300 text-xs mt-1 flex items-center">
                                  <Building className="h-3 w-3 mr-1" />
                                  {event.company}
                                </div>
                              )}
                              {event.type === 'recurring_payment' && event.domain && (
                                <div className="text-green-600 dark:text-green-400 text-xs mt-0.5 flex items-center">
                                  <Globe className="h-3 w-3 mr-1" />
                                  {event.domain}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-900 dark:text-green-200 text-sm">
                                {formatCurrency(event.amount, event.currency)}
                              </div>
                            </div>
                          </div>
                          
                          {/* Detaylı Bilgiler */}
                          <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-700 space-y-1">
                            {event.type === 'receivable' && (
                              <>
                                {event.customer_email && (
                                  <div className="text-green-600 dark:text-green-400 text-xs flex items-center">
                                    <span className="w-12 sm:w-16 text-green-500 dark:text-green-500">{t('calendar.email')}:</span>
                                    <span className="flex-1 truncate">{event.customer_email}</span>
                                  </div>
                                )}
                                {event.customer_phone && (
                                  <div className="text-green-600 dark:text-green-400 text-xs flex items-center">
                                    <span className="w-12 sm:w-16 text-green-500 dark:text-green-500">{t('calendar.phone')}:</span>
                                    <span className="flex-1 truncate">{event.customer_phone}</span>
                                  </div>
                                )}
                                {event.status && (
                                  <div className="text-green-600 dark:text-green-400 text-xs flex items-center">
                                    <span className="w-12 sm:w-16 text-green-500 dark:text-green-500">{t('calendar.status')}:</span>
                                    <span className={`flex-1 px-2 py-0.5 rounded text-xs ${
                                      event.status === 'received' 
                                        ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                                        : event.status === 'pending'
                                        ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                    }`}>
                                      {event.status === 'received' ? t('calendar.statusReceived') :
                                       event.status === 'pending' ? t('calendar.statusPending') :
                                       event.status === 'overdue' ? t('calendar.statusOverdue') : event.status}
                                    </span>
                                  </div>
                                )}
                                {event.received_date && (
                                  <div className="text-green-600 dark:text-green-400 text-xs flex items-center">
                                    <span className="w-12 sm:w-16 text-green-500 dark:text-green-500">{t('calendar.received')}:</span>
                                    <span className="flex-1">{new Date(event.received_date).toLocaleDateString('tr-TR')}</span>
                                  </div>
                                )}
                              </>
                              )}
                              {event.type === 'recurring_payment' && event.frequency && (
                                <div className="text-green-600 dark:text-green-400 text-xs flex items-center">
                                  <span className="w-12 sm:w-16 text-green-500 dark:text-green-500">{t('calendar.frequency', 'Sıklık')}:</span>
                                  <span className="flex-1">
                                  {event.frequency === 'monthly' ? t('calendar.monthly', 'Aylık') : 
                                   event.frequency === 'yearly' ? t('calendar.yearly', 'Yıllık') : 
                                   event.frequency === 'one-time' ? t('calendar.oneTime', 'Tek Seferlik') : event.frequency}
                                </span>
                              </div>
                            )}
                            {event.type === 'payment_due' && event.invoice_number && (
                              <div className="text-green-600 dark:text-green-400 text-xs flex items-center">
                                <span className="w-12 sm:w-16 text-green-500 dark:text-green-500">{t('calendar.invoice')}:</span>
                                <span className="flex-1 truncate">{event.invoice_number}</span>
                              </div>
                            )}
                            {event.notes && (
                              <div className="text-green-600 dark:text-green-400 text-xs">
                                <span className="text-green-500 dark:text-green-500">{t('calendar.notes')}:</span> {event.notes}
                              </div>
                            )}
                            {event.description && event.type !== 'receivable' && (
                              <div className="text-green-600 dark:text-green-400 text-xs">
                                <span className="text-green-500 dark:text-green-500">{t('calendar.description')}:</span> {event.description}
                              </div>
                            )}
                          </div>
                          
                          {googleCalendarEnabled && (
                            <button
                              onClick={() => addToGoogleCalendar(event)}
                              className="mt-2 text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 rounded flex items-center"
                              title={t('calendar.addToGoogle')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              {t('calendar.addToGoogle')}
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Verecekler */}
              {selectedEvents.filter(e => e.category === 'expense').length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    {t('calendar.payables', 'Verecekler')}
                  </h4>
                  <div className="space-y-2">
                    {selectedEvents
                      .filter(e => e.category === 'expense')
                      .map(event => (
                        <div
                          key={event.id}
                          className={`
                            p-3 rounded-lg border
                            ${event.type === 'recurring_payment' 
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                              : event.type === 'server_payment'
                              ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }
                          `}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-sm mb-1 flex items-center">
                                {getEventIcon(event.type)}
                                <span className="ml-1">
                                  {event.type === 'recurring_payment' ? t('calendar.recurringPayment') :
                                   event.type === 'server_payment' ? t('calendar.serverPayment') :
                                   t('calendar.payment')}
                                </span>
                              </div>
                              <div className="text-sm font-medium mb-1">
                                {event.title}
                              </div>
                              {event.company && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {t('calendar.company')}: {event.company}
                                </div>
                              )}
                              {event.domain && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {t('calendar.domain')}: {event.domain}
                                </div>
                              )}
                              {event.provider && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {t('calendar.provider')}: {event.provider}
                                </div>
                              )}
                              {event.frequency && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {t('calendar.frequency', 'Sıklık')}: {event.frequency === 'monthly' ? t('calendar.monthly', 'Aylık') :
                                          event.frequency === 'yearly' ? t('calendar.yearly', 'Yıllık') :
                                          event.frequency === 'one-time' ? t('calendar.oneTime', 'Tek Seferlik') :
                                          event.frequency}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-sm">
                                {formatCurrency(event.amount, event.currency)}
                              </div>
                            </div>
                          </div>
                          {event.notes && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                              {event.notes}
                            </div>
                          )}
                          {googleCalendarEnabled && (
                            <button
                              onClick={() => addToGoogleCalendar(event)}
                              className="mt-2 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded flex items-center"
                              title={t('calendar.addToGoogle')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              {t('calendar.addToGoogle')}
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;

